// frontend/src/pages/AdminNotificationsPage.tsx
//
// Admin broadcast composer + management view (#211 Phase B).
//
// Three sections:
//   1. Compose form — message, severity, recipient_type, optional expires_at,
//      bypass_preferences toggle. Submits POST /admin/notifications.
//   2. Active broadcasts list — broadcasts with recalled_at IS NULL.
//      Each row has a Recall button which calls DELETE /admin/notifications/:id.
//   3. Recalled broadcasts list — audit-only view showing recalled broadcasts
//      with their recalled_at + recalled_by_user_id. No actions, just visibility.
//
// API surface consumed:
//   GET    /admin/notifications        list all (active + recalled)
//   POST   /admin/notifications        create broadcast (super_admin only)
//   DELETE /admin/notifications/:id    recall broadcast (super_admin only)
//
// Single-user targeting (recipient_type='user') is intentionally NOT exposed in
// this UI for Day 15 — the backend accepts it for schema consistency, but the
// admin UI shows only 'all' and 'role' until a future ticket adds user search.

import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  CampaignOutlined,
  Refresh,
  Send,
  UndoOutlined,
} from '@mui/icons-material';
import { api } from '../utils/api';

type Severity = 'info' | 'warning';
type RecipientType = 'all' | 'user' | 'role';
type Category =
  | 'security'
  | 'maintenance'
  | 'site_status'
  | 'feature_announcement'
  | 'marketing'
  | 'travel_reminder';

// Categories that force bypass_preferences=true on the server. The frontend
// mirrors this rule for UX (auto-flip the Switch + disable it) but the
// backend is the source of truth.
const CLICK_ONLY_CATEGORIES: ReadonlyArray<Category> = [
  'security',
  'maintenance',
  'site_status',
];

// Categories the admin compose UI exposes. travel_reminder is auto-generated
// by the Phase 5 scheduled worker (not admin-composable).
const ADMIN_SELECTABLE_CATEGORIES: ReadonlyArray<Category> = [
  'security',
  'maintenance',
  'site_status',
  'feature_announcement',
  'marketing',
];

const CATEGORY_LABELS: Record<Category, string> = {
  security: 'Security',
  maintenance: 'Maintenance',
  site_status: 'Site status',
  feature_announcement: 'Feature announcement',
  marketing: 'Marketing',
  travel_reminder: 'Travel reminder',
};

// Phase 1: neutral-ish chips for the admin tables. The user-facing visual
// treatment from spec §3.1 (red/yellow/blue/etc.) lives in the bell rendering
// in Phase 3, not here — admins see their own work, not end-user surfaces.
const categoryChipColor = (
  c: Category
): 'default' | 'error' | 'warning' | 'info' | 'primary' => {
  switch (c) {
    case 'security':
      return 'error';
    case 'maintenance':
    case 'site_status':
      return 'warning';
    case 'feature_announcement':
      return 'info';
    case 'marketing':
      return 'default';
    case 'travel_reminder':
      return 'primary';
  }
};

interface AdminBroadcast {
  notification_id: string;
  message: string;
  severity: Severity;
  deep_link: string | null;
  recipient_type: RecipientType;
  target_user_id: string | null;
  target_role: string | null;
  bypass_preferences: boolean;
  category: Category;
  sent_at: string;
  sent_by_user_id: string;
  expires_at: string | null;
  recalled_at: string | null;
  recalled_by_user_id: string | null;
}

interface ListResponse {
  success: boolean;
  notifications: AdminBroadcast[];
  pagination?: { limit: number; offset: number; returned: number };
}

interface CreateResponse {
  success: boolean;
  notification_id?: string;
  sent_at?: string;
  error?: string;
}

interface RecallResponse {
  success: boolean;
  notification_id?: string;
  recalled_at?: string;
  already_recalled?: boolean;
  error?: string;
}

const formatTimestamp = (iso: string | null): string => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

const severityColor = (s: Severity): 'info' | 'warning' => s;

const recipientLabel = (b: AdminBroadcast): string => {
  if (b.recipient_type === 'all') return 'All users';
  if (b.recipient_type === 'role' && b.target_role) return `Role: ${b.target_role}`;
  if (b.recipient_type === 'user' && b.target_user_id) {
    return `User: ${b.target_user_id.slice(0, 8)}…`;
  }
  return b.recipient_type;
};

const AdminNotificationsPage: React.FC = () => {
  // ─── List state ────────────────────────────────────────────────────────
  const [broadcasts, setBroadcasts] = useState<AdminBroadcast[]>([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState<string | null>(null);

  // ─── Compose form state ────────────────────────────────────────────────
  const [message, setMessage] = useState('');
  const [severity, setSeverity] = useState<Severity>('info');
  const [recipientType, setRecipientType] = useState<RecipientType>('all');
  const [targetRole, setTargetRole] = useState<string>('user');
  const [bypassPreferences, setBypassPreferences] = useState(false);
  const [expiresAt, setExpiresAt] = useState<string>(''); // datetime-local format
  const [category, setCategory] = useState<Category>('feature_announcement');

  // Click-only categories force bypass_preferences. We track this locally so
  // the Switch reflects the locked-true state and disables; the backend
  // re-enforces the rule independently.
  const bypassLocked = CLICK_ONLY_CATEGORIES.includes(category);
  const effectiveBypass = bypassLocked ? true : bypassPreferences;

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState<string | null>(null);

  // ─── Recall confirmation state ─────────────────────────────────────────
  const [recallDialog, setRecallDialog] = useState<AdminBroadcast | null>(null);
  const [recalling, setRecalling] = useState(false);
  const [recallError, setRecallError] = useState<string | null>(null);

  // ─── Derived lists ─────────────────────────────────────────────────────
  const { active, recalled } = useMemo(() => {
    const a: AdminBroadcast[] = [];
    const r: AdminBroadcast[] = [];
    for (const b of broadcasts) {
      if (b.recalled_at) r.push(b);
      else a.push(b);
    }
    return { active: a, recalled: r };
  }, [broadcasts]);

  // ─── Load broadcasts ───────────────────────────────────────────────────
  const loadBroadcasts = async () => {
    setLoading(true);
    setListError(null);
    try {
      const res = await api.get('/admin/notifications');
      if (!res.ok) {
        setListError(`Failed to load broadcasts (HTTP ${res.status})`);
        setBroadcasts([]);
        return;
      }
      const data: ListResponse = await res.json();
      if (!data.success) {
        setListError('Backend returned success=false');
        setBroadcasts([]);
        return;
      }
      setBroadcasts(data.notifications || []);
    } catch (err: any) {
      setListError(err?.message || 'Network error loading broadcasts');
      setBroadcasts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBroadcasts();
  }, []);

  // ─── Compose form actions ──────────────────────────────────────────────
  const resetForm = () => {
    setMessage('');
    setSeverity('info');
    setRecipientType('all');
    setTargetRole('user');
    setBypassPreferences(false);
    setExpiresAt('');
    setCategory('feature_announcement');
  };

  const validateForm = (): string | null => {
    if (!message.trim()) return 'Message is required';
    if (message.length > 2000) return 'Message must be 2000 characters or fewer';
    if (recipientType === 'role' && !targetRole) return 'Target role is required when recipient type is role';
    if (expiresAt) {
      const ts = new Date(expiresAt);
      if (Number.isNaN(ts.getTime())) return 'Expires-at is not a valid date';
      if (ts.getTime() <= Date.now()) return 'Expires-at must be in the future';
    }
    return null;
  };

  const handleSubmit = async () => {
    const validation = validateForm();
    if (validation) {
      setSubmitError(validation);
      return;
    }

    setSubmitting(true);
    setSubmitError(null);
    setSubmitSuccess(null);

    const body: Record<string, unknown> = {
      message: message.trim(),
      severity,
      recipient_type: recipientType,
      bypass_preferences: effectiveBypass,
      category,
    };
    if (recipientType === 'role') body.target_role = targetRole;
    if (expiresAt) body.expires_at = new Date(expiresAt).toISOString();

    try {
      const res = await api.post('/admin/notifications', body);
      const data: CreateResponse = await res.json();
      if (!res.ok || !data.success) {
        setSubmitError(data?.error || `Submit failed (HTTP ${res.status})`);
        return;
      }
      setSubmitSuccess(`Broadcast sent (id ${data.notification_id?.slice(0, 8)}…)`);
      resetForm();
      // Refresh list so the new broadcast appears immediately
      await loadBroadcasts();
    } catch (err: any) {
      setSubmitError(err?.message || 'Network error submitting broadcast');
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Recall actions ────────────────────────────────────────────────────
  const handleRecallConfirm = async () => {
    if (!recallDialog) return;
    setRecalling(true);
    setRecallError(null);
    try {
      const res = await api.delete(`/admin/notifications/${recallDialog.notification_id}`);
      const data: RecallResponse = await res.json();
      if (!res.ok || !data.success) {
        setRecallError(data?.error || `Recall failed (HTTP ${res.status})`);
        return;
      }
      // Close dialog and refresh
      setRecallDialog(null);
      await loadBroadcasts();
    } catch (err: any) {
      setRecallError(err?.message || 'Network error during recall');
    } finally {
      setRecalling(false);
    }
  };

  // ─── Render ────────────────────────────────────────────────────────────
  return (
    <Box>
      {/* Header */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={600} gutterBottom>
            Admin Notifications
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Send broadcasts to users in the bell. Recalls are audited — broadcasts remain in the management list with the recall timestamp + recaller for compliance.
          </Typography>
        </Box>
        <Tooltip title="Refresh">
          <IconButton onClick={loadBroadcasts} disabled={loading}>
            <Refresh />
          </IconButton>
        </Tooltip>
      </Stack>

      {/* Compose form */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
            <CampaignOutlined color="primary" />
            <Typography variant="h6" fontWeight={600}>
              Compose Broadcast
            </Typography>
          </Stack>

          {submitError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSubmitError(null)}>
              {submitError}
            </Alert>
          )}
          {submitSuccess && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSubmitSuccess(null)}>
              {submitSuccess}
            </Alert>
          )}

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="Message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                fullWidth
                multiline
                minRows={3}
                maxRows={8}
                inputProps={{ maxLength: 2000 }}
                helperText={`${message.length} / 2000 characters`}
                disabled={submitting}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth disabled={submitting}>
                <InputLabel id="severity-label">Severity</InputLabel>
                <Select
                  labelId="severity-label"
                  label="Severity"
                  value={severity}
                  onChange={(e) => setSeverity(e.target.value as Severity)}
                >
                  <MenuItem value="info">Info</MenuItem>
                  <MenuItem value="warning">Warning</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth disabled={submitting}>
                <InputLabel id="category-label">Category</InputLabel>
                <Select
                  labelId="category-label"
                  label="Category"
                  value={category}
                  onChange={(e) => setCategory(e.target.value as Category)}
                >
                  {ADMIN_SELECTABLE_CATEGORIES.map((c) => (
                    <MenuItem key={c} value={c}>
                      {CATEGORY_LABELS[c]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth disabled={submitting}>
                <InputLabel id="recipient-label">Recipient</InputLabel>
                <Select
                  labelId="recipient-label"
                  label="Recipient"
                  value={recipientType}
                  onChange={(e) => setRecipientType(e.target.value as RecipientType)}
                >
                  <MenuItem value="all">All users</MenuItem>
                  <MenuItem value="role">By role</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {recipientType === 'role' && (
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth disabled={submitting}>
                  <InputLabel id="role-label">Role</InputLabel>
                  <Select
                    labelId="role-label"
                    label="Role"
                    value={targetRole}
                    onChange={(e) => setTargetRole(e.target.value as string)}
                  >
                    <MenuItem value="user">User</MenuItem>
                    <MenuItem value="admin">Admin</MenuItem>
                    <MenuItem value="super_admin">Super Admin</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            )}

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Expires at (optional)"
                type="datetime-local"
                value={expiresAt}
                onChange={(e) => setExpiresAt(e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
                disabled={submitting}
              />
            </Grid>

            <Grid item xs={12}>
              <Tooltip
                title={
                  bypassLocked
                    ? ` broadcasts always bypass user preferences.`
                    : ''
                }
                placement="top-start"
                arrow
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={effectiveBypass}
                      onChange={(e) => setBypassPreferences(e.target.checked)}
                      disabled={submitting || bypassLocked}
                    />
                  }
                  label="Bypass user preferences (deliver even if app notifications are off)"
                />
              </Tooltip>
            </Grid>

            <Grid item xs={12}>
              <Stack direction="row" spacing={2}>
                <Button
                  variant="contained"
                  startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <Send />}
                  onClick={handleSubmit}
                  disabled={submitting || !message.trim()}
                >
                  {submitting ? 'Sending…' : 'Send Broadcast'}
                </Button>
                <Button variant="text" onClick={resetForm} disabled={submitting}>
                  Reset
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Active broadcasts */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
            Active Broadcasts ({active.length})
          </Typography>

          {listError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {listError}
            </Alert>
          )}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : active.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
              No active broadcasts.
            </Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Message</TableCell>
                    <TableCell>Severity</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell>Recipient</TableCell>
                    <TableCell>Bypass</TableCell>
                    <TableCell>Sent</TableCell>
                    <TableCell>Expires</TableCell>
                    <TableCell align="right">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {active.map((b) => (
                    <TableRow key={b.notification_id} hover>
                      <TableCell sx={{ maxWidth: 360 }}>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                          {b.message}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={b.severity}
                          color={severityColor(b.severity)}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={CATEGORY_LABELS[b.category]}
                          color={categoryChipColor(b.category)}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>{recipientLabel(b)}</TableCell>
                      <TableCell>
                        {b.bypass_preferences ? (
                          <Chip size="small" label="Yes" color="warning" />
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell>{formatTimestamp(b.sent_at)}</TableCell>
                      <TableCell>{formatTimestamp(b.expires_at)}</TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          color="error"
                          variant="outlined"
                          startIcon={<UndoOutlined />}
                          onClick={() => setRecallDialog(b)}
                        >
                          Recall
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Recalled broadcasts (audit view) */}
      <Card>
        <CardContent>
          <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
            Recalled Broadcasts ({recalled.length})
          </Typography>

          {recalled.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
              No recalled broadcasts.
            </Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Message</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell>Recipient</TableCell>
                    <TableCell>Sent</TableCell>
                    <TableCell>Recalled</TableCell>
                    <TableCell>Recalled By</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recalled.map((b) => (
                    <TableRow key={b.notification_id}>
                      <TableCell sx={{ maxWidth: 360 }}>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: 'text.secondary' }}>
                          {b.message}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={CATEGORY_LABELS[b.category]}
                          color={categoryChipColor(b.category)}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>{recipientLabel(b)}</TableCell>
                      <TableCell>{formatTimestamp(b.sent_at)}</TableCell>
                      <TableCell>{formatTimestamp(b.recalled_at)}</TableCell>
                      <TableCell>
                        {b.recalled_by_user_id ? `${b.recalled_by_user_id.slice(0, 8)}…` : '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Recall confirmation dialog */}
      <Dialog open={!!recallDialog} onClose={() => !recalling && setRecallDialog(null)}>
        <DialogTitle>Recall this broadcast?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            The broadcast will stop appearing for users who haven't dismissed it. The audit row stays in place — your user_id and the recall timestamp will be recorded.
          </DialogContentText>
          {recallDialog && (
            <Paper variant="outlined" sx={{ mt: 2, p: 2, bgcolor: 'grey.50' }}>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {recallDialog.message}
              </Typography>
            </Paper>
          )}
          {recallError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {recallError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRecallDialog(null)} disabled={recalling}>
            Cancel
          </Button>
          <Button
            onClick={handleRecallConfirm}
            color="error"
            variant="contained"
            disabled={recalling}
            startIcon={recalling ? <CircularProgress size={16} color="inherit" /> : <UndoOutlined />}
          >
            {recalling ? 'Recalling…' : 'Recall Broadcast'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminNotificationsPage;
